import React from "react";
import { Users, Music4, Share2, CalendarClock } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import PerformForm from "../components/PerformForm";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { UPCOMING_EDITION, PERFORM, INTEREST_HREF } from "../lib/constants";

/**
 * Open call for dancers — Chandigarh, 25 October 2026.
 *
 * This is an UNPAID showcase and the page says so plainly, above the fold and
 * again next to the form. Burying that is how open calls earn a backlash; the
 * honest version is also the one that attracts people who actually want the
 * room rather than a fee.
 */
const GETS = [
  { icon: Users, title: "An 800-seat theatre", body: "Tagore Theatre, Sector 18. A full hall, already singing before you begin." },
  { icon: Music4, title: "A live band, not a track", body: "You will dance to musicians playing behind you, not to recorded music." },
  { icon: Share2, title: "Seen beyond the hall", body: "Your set goes out after the show to a combined following of 98,000 across the Antara and Kashi with Anshu pages." },
  { icon: CalendarClock, title: "A real stage", body: "Professional light and sound, and an audience that came to listen." },
];

export default function Perform() {
  const e = UPCOMING_EDITION;
  if (!PERFORM.open) return <PerformClosed />;
  return (
    <>
      <Seo route={ROUTES.perform} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.perform.crumb, path: ROUTES.perform.path }])} />

      <PageHero
        eyebrow="Open call · Chandigarh"
        title={<>A place on the <span className="gold-grad italic">Antara stage</span></>}
        intro={`We are looking for solo dancers and dance groups — any style that sits with a bhajan. ${e.dateLabel}, ${e.venue}, ${e.venueStreet}. Applications close ${PERFORM.deadlineLabel}.`}
      >
        <div className="mt-8 flex flex-col items-center">
          <a href="#apply" className="cta-primary" data-testid="perform-hero-btn">
            Apply to perform <span className="cta-arrow">→</span>
          </a>
          <p className="mt-3 font-body text-sm font-medium" style={{ color: "#E5C880" }}>
            An unpaid showcase. We do not pay performers, and we do not charge them.
          </p>
        </div>
      </PageHero>

      {/* What the performer actually gets */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow>What you get</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.05] text-ink">
                Not a fee. <span className="gold-grad italic">This.</span>
              </h2>
              <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
                We would rather say it plainly than bury it: there is no fee for
                this showcase. What there is instead is written below, and all of
                it is real.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 gap-5 md:gap-6">
            {GETS.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.1}>
                <div className="card-lift h-full p-7 md:p-8 rounded-2xl bg-ivory-2 border border-gold/25">
                  <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center bg-gradient-to-br from-[#E5C880] to-[#B8923E]">
                    <g.icon size={22} strokeWidth={1.4} color="#2A1F17" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl text-ink leading-tight">{g.title}</h3>
                  <p className="mt-3 font-body text-sm md:text-base text-ink-2/85 leading-relaxed">{g.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The form */}
      <section id="apply" className="scroll-mt-24 bg-[#120C08] py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>How to apply</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.05] text-[#F5EFE6]">
                Send us a <span className="gold-grad italic">video</span>
              </h2>
              <p className="mt-6 font-body text-base md:text-lg text-[#E8D9C5]/80 leading-relaxed">
                Under two minutes. A rehearsal clip is perfectly good — we are
                watching you dance, not your production. Applications are open
                until {PERFORM.deadlineLabel}.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <PerformForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}

/** The call has closed: say so plainly, and point to what is still open. */
function PerformClosed() {
  return (
    <>
      <Seo route={ROUTES.perform} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.perform.crumb, path: ROUTES.perform.path }])} />
      <PageHero
        eyebrow="Open call · Closed"
        title={<>Applications have <span className="gold-grad italic">closed</span></>}
        intro={`The open call for dancers at the ${UPCOMING_EDITION.city} edition closed on ${PERFORM.deadlineLabel}. Thank you to everyone who sent a video.`}
      >
        <div className="mt-8 flex flex-col items-center" data-testid="perform-closed">
          <a href={INTEREST_HREF} className="cta-primary">
            Hear about the next edition <span className="cta-arrow">→</span>
          </a>
          <p className="mt-3 font-body text-sm" style={{ color: "#E5C880" }}>
            The next open call goes to the interest list first.
          </p>
        </div>
      </PageHero>
    </>
  );
}
